import { useTranslation, Trans } from 'react-i18next'
import Alert from '@mui/material/Alert'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartOutlined'

import { useDateFormatter } from '../../../utils/date'
import { type Quote } from '../../../modules/quotes'
import { type QuoteAlternativeDate } from '../../../modules/quotes/core/models/subModels/alternativeDate.model'
import { useCurrentMove, useSelectQuoteAction, useUpdateMoveAction } from '../../../modules/moves'
import { PageContent } from '../../../components/Container'
import MoveRecapBanner from '../../../components/MoveSummaryBanner'
import ContainedPage from '../../../components/layout/ContainedPage'
import { Quotes } from '../../../components/QuoteComponent'
import { Heading, Subtitle } from '../../../components/typography'
import { useErrorFormatter } from '../../../components/errors/useErrorFormatter'
import Spinner from '../../../components/Spinner'
import { errorNotification } from '../../../components/ToastNotifications'
import Button, { ButtonSize, ButtonVariant } from '../../../components/Button'
import ConfirmationModal, { useConfirmation } from '../../../components/ConfirmationModal'

const QuotesPage: React.FC = () => {
  const { t } = useTranslation()
  const move = useCurrentMove()
  const [selectQuote, { loading: selectingQuote }] = useSelectQuoteAction()
  const [updateMove, { loading: updatingMove }] = useUpdateMoveAction()
  const formatError = useErrorFormatter()
  const { confirm, modalProps } = useConfirmation()
  const formatDate = useDateFormatter()

  const loading = selectingQuote || updatingMove
  const singleDayMove = move.jobs.length === 1

  const onSelectQuote = async (quote: Quote, position: number) => {
    try {
      await selectQuote(quote, position)
    } catch (error) {
      errorNotification(formatError(error))
    }
  }

  const handleDateChange = async (jobId: string, { date }: QuoteAlternativeDate) => {
    const MoveDate = () => <strong>{ formatDate(date, { showYear: false }) }</strong>

    if (!await confirm({
      title: t('quotes.updateDate.confirmation.title'),
      question: (
        <>
          <Trans
            i18nKey="quotes.updateDate.confirmation.question"
          >
            <MoveDate />
          </Trans>

          <br /><br />
          <Alert severity="info">
            { t('quotes.updateDate.confirmation.trafficWarning') }
          </Alert>
        </>
      ),
    })) {
      return
    }

    try {
      const startDate = new Date(date).toISOString().substring(0, 10)
      await updateMove({
        jobs: [
          {
            id: move.jobs[0].id,
            dateTime: {
              startDate,
              preferredTimeRanges: move.jobs[0].dateTime.preferredTimeRanges ?? [],
            },
          },
        ],
      })
    } catch (error) {
      errorNotification(formatError(error))
    }
  }

  return (
    <ContainedPage>
      <PageContent topContent={<MoveRecapBanner />}>
        { loading && (
          <Spinner floating backdrop />
        ) }
        <div className="mb-12 mt-2 text-center">
          <Heading variant="h1">
            { t('pages.move.quotes.title') }
          </Heading>
          <Subtitle className="lg:text-center">
            { t('pages.move.quotes.subtitleLine1') }
            <br />
            { t('pages.move.quotes.subtitleLine2') }
          </Subtitle>
        </div>

        { move.quotes && (
          <Quotes
            move={move}
            showBest
            customerPerspective
            docTop={70}
            onAlternativeDateClick={singleDayMove ? handleDateChange : undefined}
            cta={(quote: Quote, position: number) => (
              <Button
                variant={ButtonVariant.Filled}
                size={ButtonSize.Small}
                onClick={async () => { await onSelectQuote(quote, position) }}
                className="md:!mb-[-10px]"
              >
                <ShoppingCartIcon className="mr-[5px] mt-[-5px] !text-[18px]" />
                { t('pages.move.quotes.selectQuote') }
              </Button>
            )}
          />
        ) }

        <ConfirmationModal {...modalProps} />
      </PageContent>
    </ContainedPage>
  )
}

export default QuotesPage
